import { FC, useState, useMemo, useEffect } from 'react'
import React from 'react'
import { KTSVG } from '../../../../_metronic/helpers'
import { useAuth } from '../../auth'
import {
  listInspectionKinds, listPorts,  // <- use listPorts()
  searchInspections, createInspection, mapInspectionToRow,
  listCrewsLite, linkPlanToInspection, unlinkPlan,
  getInspection, updateInspection, uploadInspectionAttachments,
  deleteInspection
} from '../core/_requests'
import { getCompanyList, getCompanyAdminList } from '../../Management/core/_requests'
import type { Vessel } from '../../Management/core/_models'
import type {
  QhseInspectionKind,
  CreateInspectionPayload, InspectionRecord, VesselLite, 
  InspectionDto
} from '../core/_models'
import AddInspectionModal from './AddInspectionModal'
import { ViewInspectionModal } from './ViewInspectionModal'
import { EditInspectionModal } from './EditInspectionModal'
import DeleteRecordConfirmModal from './DeleteRecordConfirmModal'
import { fetchAccessibleVesselsForCurrentUser, normalizeAccessibleVessels } from '../../../shared/access/accessibleVessels'

// import the submit type from the modal so types stay in sync
import type { SubmitShape } from './AddInspectionModal'

// Option A: if your Crewing module exposes a list endpoint, prefer that:
// import { listCrewsLite } from '../../Crewing/core/_requests' 
// (If you don’t have this exact fn, see 3.d “Adapter” below)
import type { CrewLite } from '../core/_models' // or from Crewing/_models
import { getRanksforList } from '../../Crewing/core/_requests'
import { QHSE_PERMS } from '../core/permissions'
import {
  filterVesselsForManagementList,
  getUserCompanyGroupAdminId,
  isBroadFleetPickerUser,
  isLikelyVesselAssignedUser,
} from '../core/qhseUserContext'
import { toast } from 'react-toastify'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'

const toastApiError = (e: unknown, fallback: string) => {
  const anyE = e as { response?: { status?: number; data?: { message?: string } } }
  if (anyE?.response?.status === 403) {
    toast.error('You do not have access to perform this action.', { position: 'top-center' })
    return
  }
  toast.error(fallback, { position: 'top-center' })
}

const prettyEnum = (s?: string) =>
  s ? s.toLowerCase().replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase()) : '-'

interface AddInspectionModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: (data: Omit<InspectionRecord, 'id' | 'vesselManager' | 'vesselType' | 'internalInspector' | 'isFinalized' | 'isVerified' | 'countNCRsObservations' | 'countNCRsObservationsCompleted' | 'score'>, saveAndAddNew: boolean) => void
}

const tableOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  background: 'rgba(255,255,255,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 5,
};

const spinner = (
  <div className="spinner-border" role="status" aria-label="Loading">
    <span className="visually-hidden">Loading...</span>
  </div>
);

const planOptions = [] as {id:number; label:string}[]

const InspectionIndex: FC = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [sortConfig, setSortConfig] = useState<{
    key: keyof InspectionRecord | null;
    direction: 'asc' | 'desc';
  }>({ key: null, direction: 'asc' })
  const [isModalVisible, setIsModalVisible] = useState(false)
  // Filters (ids as strings for selects)
const [filters, setFilters] = useState({
  companyId: '',       // Company Group (CGA)
  subcompanyId: '',    // Company (Subcompany)
  vesselId: '',        // Vessel
  vesselType: '',
  inspection: '',
  inspectionType: '',
  internalInspector: '',
  externalInspector: '',
  inspectionFromDate: '',
  inspectionToDate: '',
  fromPort: '',
  toPort: '',
  activeStatus: 'All', // All / Show Active / Show Inactive
  isFinalized: 'All',  // All / Yes / No
  isVerified: 'All',   // All / Yes / No
})

// filters stay as-is
const [companies, setCompanies] = useState<Array<{id:number; name:string}>>([])
const [subcompanies, setSubcompanies] = useState<Array<{id:number; name:string; companyId:number}>>([])
const [vessels, setVessels] = useState<VesselLite[]>([])
const [ports, setPorts] = useState<Array<{id:number; name:string}>>([])
const [kinds, setKinds] = useState<QhseInspectionKind[]>([])

// NEW: id->name maps to avoid O(n^2) scanning
const [vesselMap, setVesselMap] = useState<Record<number, string>>({})
const [kindMap, setKindMap]     = useState<Record<number, string>>({})
const [portMap, setPortMap]     = useState<Record<number, string>>({})

// NEW: id->vesselType map
const [vesselTypeMap, setVesselTypeMap] = useState<Record<number, string>>({})

  const [showModal, setShowModal] = useState(false);

  const { currentUser, can, permissionsLoaded, isSuperAdmin } = useAuth()

  const [rankMap, setRankMap] = useState<Record<number, string>>({})

  const [lookupsReady, setLookupsReady] = useState(false);

  const shipboardUser = isLikelyVesselAssignedUser(currentUser)
  const broadFleet = isBroadFleetPickerUser(currentUser)
  const myCgaId = getUserCompanyGroupAdminId(currentUser)
  const isTopLevel = broadFleet
  const effectiveCompanyId = isTopLevel ? undefined : (myCgaId ?? undefined)

  const canReadInspection = permissionsLoaded && can(QHSE_PERMS.INSPECTION_READ)
  const canCreateInspection = permissionsLoaded && can(QHSE_PERMS.INSPECTION_CREATE)
  const canUpdateInspection = permissionsLoaded && can(QHSE_PERMS.INSPECTION_UPDATE)
  const canDeleteInspection = isSuperAdmin || (permissionsLoaded && can(QHSE_PERMS.INSPECTION_DELETE))

  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [recordToDelete, setRecordToDelete] = useState<InspectionRecord | null>(null)
  const [deleteFindingsCount, setDeleteFindingsCount] = useState(0)
  const [deleteFindingAttachmentsCount, setDeleteFindingAttachmentsCount] = useState(0)
  const [deleteLoading, setDeleteLoading] = useState(false)

const [inspectionData, setInspectionData] = useState<InspectionRecord[]>([])
const [loading, setLoading] = useState(false)

const [crews, setCrews] = useState<CrewLite[]>([]) // NEW

// View / Edit modal state
const [viewOpen, setViewOpen] = useState(false)
const [editOpen, setEditOpen] = useState(false)
const [viewing, setViewing] = useState<InspectionDto | null>(null)
const [editing, setEditing] = useState<InspectionDto | null>(null)

const [bootLoading, setBootLoading] = useState(true);        // until 1st load finishes
const [noDataDelayPassed, setNoDataDelayPassed] = useState(false); // short delay before showing empty
  const [didAutoSelectVessel, setDidAutoSelectVessel] = useState(false)

// Auto-fetch when company/subcompany/vessel or date/kind filters change (like Rest Hours behavior)
useEffect(() => {
  if (!lookupsReady) return;           // <-- gate
  loadInspections();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [
  lookupsReady,                         // <-- include the flag
  filters.companyId,
  filters.subcompanyId,
  filters.vesselId,
  filters.inspectionFromDate,
  filters.inspectionToDate,
  filters.inspection,
  filters.inspectionType,
  filters.fromPort,
  filters.toPort,
  filters.isFinalized,
  filters.isVerified,
  filters.activeStatus
]);

const niceEnum = (s?: string) =>
  s ? s.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '-'


useEffect(() => {
  const loadLookups = async () => {
    try {
      // simple lookups
      const [k, p] = await Promise.all([listInspectionKinds(), listPorts()])
      setKinds(k); setPorts(p)

      // 🔧 ADD inside loadLookups() before/after other Promise.all:
const ranks = await getRanksforList().catch(() => [])
const rMap: Record<number,string> = {}
;(Array.isArray(ranks) ? ranks : []).forEach((r: any) => {
  if (r?.id != null) rMap[Number(r.id)] = String(r.rank ?? r.name ?? '')
})
setRankMap(rMap)


      // vessel list (same pattern as Vessel Certificates / RestHours)
      const vesselList: Vessel[] = normalizeAccessibleVessels(await fetchAccessibleVesselsForCurrentUser())

     // ---- Companies & Subcompanies (robust like RestHours) ----
const [groups, admins] = await Promise.all([
  getCompanyAdminList?.().catch(() => []), // CGA
  getCompanyList?.().catch(() => []),      // Company Admins
])

setCompanies(
  Array.isArray(groups)
    ? groups.map((g: any) => ({
        id: Number(g.id),
        name: g.name ?? g.companyGroupAdminName ?? `-`,
      }))
    : []
)

/**
 * Some backends omit /users/company-admins details or return empty.
 * We derive subcompanies from the vessel list as a fallback and merge.
 */
const derivedFromVessels = (vesselList || [])
  .map((v: any) => ({
    id: Number(v?.companyAdmin?.id ?? v?.companyId),
    name:
      v?.companyAdmin?.name ??
      v?.companyAdminName ??
      v?.companyName ??
      (v?.companyAdmin?.id ? `-` : null),
    companyId: Number(
      v?.companyGroupAdmin?.id ??
      v?.companyGroupId ??
      v?.cgaid?.id
    ),
  }))
  .filter(sc => Number.isFinite(sc.id) && !!sc.name && Number.isFinite(sc.companyId))

// from API
const fromAdmins = Array.isArray(admins)
  ? admins.map((a: any) => ({
      id: Number(a.id),
      name: a.name ?? a.companyAdminName ?? `-`,
      // IMPORTANT: companyId here means CGA id (for cascading)
      companyId: Number(
        a.cgaid?.id ??
        a.cga?.id ??
        a.companyGroupAdminId ??
        a.companyGroupId
      ),
    }))
  : []

// merge & de-dupe by id
const subMergedMap = new Map<number, {id:number; name:string; companyId:number}>()
;[...fromAdmins, ...derivedFromVessels].forEach(sc => {
  if (!Number.isFinite(sc.id) || !Number.isFinite(sc.companyId)) return
  const prev = subMergedMap.get(sc.id)
  subMergedMap.set(sc.id, prev ? prev : sc)
})
setSubcompanies(Array.from(subMergedMap.values()))


      const vesselsForUser = filterVesselsForManagementList(vesselList, currentUser)

      setVessels(
        vesselsForUser.map((v:any) => ({
          id: Number(v.id),
          name: v.fleet_name || v.name || `Vessel ${v.id}`,
          vesselType: v.vesselType || v.type || undefined,
          companyGroupAdminId: Number(v?.companyGroupAdmin?.id ?? v?.companyGroupId),
          companyAdminId:      Number(v?.companyAdmin?.id      ?? v?.companyId),
        }))
      )

      // Build a fast lookup: vesselId -> { cgaId, caId }
const vById: Record<number, {cgaId?: number; caId?: number}> = {}
for (const v of vesselsForUser) {
  const id = Number(v.id)
  vById[id] = {
    cgaId: Number((v as any)?.companyGroupAdmin?.id ?? (v as any)?.companyGroupId) || undefined,
    caId:  Number((v as any)?.companyAdmin?.id      ?? (v as any)?.companyId)      || undefined,
  }
}
;(window as any).__INS_VMAP__ = vById   // optional: debug helper

      // build maps once
      const vMap: Record<number,string> = {}
      const vtMap: Record<number,string> = {}
      for (const v of vesselsForUser as any[]) {
  const id = Number(v.id)
  vMap[id] = v.fleet_name || v.name || `Vessel ${id}`

  // Some payloads use vesselType, some use type, occasionally vessel_type
  const rawType = v.vesselType ?? v.type ?? v.vessel_type ?? ''
  vtMap[id] = rawType ? String(rawType) : ''
}
      const kMap: Record<number,string> = {}
      for (const kind of k) kMap[Number(kind.id)] = String(kind.name || '')
      const pMap: Record<number,string> = {}
      for (const port of p) pMap[Number(port.id)] = String(port.name || '')

        
      setVesselMap(vMap)
      setVesselTypeMap(vtMap)
      setKindMap(kMap)
      setPortMap(pMap)
      
      try {
  // listCrewsLite already returns normalized objects with id, fullName,
  // rankName, rankId, vesselId, companyGroupAdminId, companyAdminId
  const rawCrews = await listCrewsLite();
  setCrews(Array.isArray(rawCrews) ? rawCrews as CrewLite[] : []);
} catch (e) {
  console.warn('Failed to load crews for internal inspector dropdown', e);
  setCrews([]);
}

setLookupsReady(true);
    } catch (e) {
      console.error('Failed to load lookups', e)
      setVessels([]); setPorts([]); setKinds([])
      setVesselMap({}); setKindMap({}); setPortMap({})
      setLookupsReady(true); 
    }
  }
  loadLookups()
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [currentUser])

// local helper inside the component
function mapWithLookups(d: InspectionDto): InspectionRecord {
  // build a light vessel meta index from current state
  const localVById: Record<number, { cgaId?: number; caId?: number }> = {}
  for (const v of vessels) {
    const id = Number(v.id)
    localVById[id] = {
      cgaId: Number((v as any).companyGroupAdminId ?? (v as any).companyGroupId) || undefined,
      caId:  Number((v as any).companyAdminId      ?? (v as any).companyId)      || undefined,
    }
  }

  const vesselIdsMeta = localVById[d.vesselId] || {}

  // Prefer server-provided names first; fallback to local maps; never show raw ids
  const vesselName =
    d.vesselName ||
    vesselMap[d.vesselId] ||
    `-`

  const kindName =
    d.inspectionKindName ||
    (d.inspectionKindId != null ? (kindMap[d.inspectionKindId] || `-`) : '-')

  const fromPortName =
    d.fromPortName ||
    (d.fromPortId != null ? (portMap[d.fromPortId] || '-') : undefined)

  const toPortName =
    d.toPortName ||
    (d.toPortId != null ? (portMap[d.toPortId] || '-') : undefined)

  const rawType = d.vesselType || vesselTypeMap[d.vesselId] || ''
  const vesselType = niceEnum(rawType) || '-'

    // Internal inspector is now a plain free-text string from backend
  const internalInspectorLabel = d.internalInspectorName || ''

  return {
    id: d.id,
    vesselManager: '-',
    vessel: vesselName,
    vesselType,
    inspection: kindName,
    inspectionType: prettyEnum(d.inspectionType),
    inspectionDate: d.inspectionFromDate,
    internalInspector: internalInspectorLabel || undefined,
    externalInspector: d.externalInspectorName || undefined,
    fromPort: fromPortName || undefined,
    toPort:   toPortName   || undefined,
    hoursOnboard: d.hoursOnboard ?? undefined,
    isFinalized: d.isFinalized ? 'Yes' : 'No',
    isVerified:  d.isVerified  ? 'Yes' : 'No',
    countNCRsObservations: d.countNcrsAndObservations ?? 0,
    countNCRsObservationsCompleted: d.countNcrsAndObservationsCompleted ?? 0,
    score: d.score ?? undefined,
    linkToPlan: d.id ? `/qhse/insplanning?inspectionId=${d.id}` : undefined,
    inspectionRemarks: d.remarks || undefined,

    // hidden ids for client filters
    vesselId: d.vesselId,
    inspectionKindId: d.inspectionKindId,
    fromPortId: d.fromPortId ?? undefined,
    toPortId: d.toPortId ?? undefined,
    companyGroupAdminId: vesselIdsMeta.cgaId ?? null,
    companyAdminId: vesselIdsMeta.caId ?? null,
    findingAttachments: d.findingAttachments || [],
    findingsCount: d.findingsCount ?? 0,
  } as InspectionRecord
}

// ⚙️ Derived vessel list for the filters (cascaded by Company -> Subcompany)
const vesselsForFilters = useMemo<VesselLite[]>(() => {
  let list = vessels
  if (filters.companyId) {
    const cid = Number(filters.companyId)
    list = list.filter(v => Number((v as any).companyGroupAdminId ?? (v as any).companyGroupId) === cid)
  }
  if (filters.subcompanyId) {
    const scid = Number(filters.subcompanyId)
    list = list.filter(v => Number((v as any).companyAdminId ?? (v as any).companyId) === scid)
  }
  return list
}, [vessels, filters.companyId, filters.subcompanyId])

// -- Subcompany helpers (only show when the chosen Company has > 0 subcompanies)
const subcompaniesForChosenCompany = useMemo(() => {
  if (!filters.companyId) return [];
  const cid = Number(filters.companyId);
  return subcompanies.filter(sc => sc.companyId === cid);
}, [subcompanies, filters.companyId]);

const hasSubcompaniesForChosenCompany = subcompaniesForChosenCompany.length > 0;

  const { state: globalState } = useGlobalFilter()

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      vesselId: globalState.vessel?.id ? String(globalState.vessel.id) : '',
      companyId: globalState.company ? String(globalState.company.id) : '',
    }))
  }, [globalState.vessel, globalState.company])

// Crew options for the filter (narrow by company/subcompany/vessel)
const crewsForFilters = useMemo(() => {
  let list = crews;

  if (filters.companyId) list = list.filter(c => c.companyGroupAdminId === Number(filters.companyId));
  if (filters.subcompanyId) list = list.filter(c => c.companyAdminId === Number(filters.subcompanyId));
  if (filters.vesselId) {
    const vid = Number(filters.vesselId);
    list = list.filter(c => !c.vesselId || c.vesselId === vid);
  }

  const seen = new Set<string>();
  const labels: string[] = [];

  for (const c of list) {
    const rank =
      c.rankName ||
      (c.rankId != null ? rankMap[c.rankId] : '') ||
      '';

    const label = rank ? `${c.fullName} — ${rank}` : c.fullName;

    if (label && !seen.has(label)) {
      seen.add(label);
      labels.push(label);
    }
  }
  return labels.sort((a, b) => a.localeCompare(b));
}, [crews, rankMap, filters.companyId, filters.subcompanyId, filters.vesselId]);

const loadInspections = async () => {
  setLoading(true)
  try {
const res = await searchInspections({
  companyGroupId: filters.companyId ? Number(filters.companyId) : effectiveCompanyId,
  companyId:      filters.subcompanyId ? Number(filters.subcompanyId) : undefined,
  vesselId:       filters.vesselId ? Number(filters.vesselId) : undefined,
  fromDate: filters.inspectionFromDate || undefined,
  toDate:   filters.inspectionToDate   || undefined,
  inspectionKindId: filters.inspection ? Number(filters.inspection) : undefined,
  inspectionType:   filters.inspectionType || undefined,
  fromPortId: filters.fromPort ? Number(filters.fromPort) : undefined,
  toPortId:   filters.toPort   ? Number(filters.toPort)   : undefined,
  isFinalized: filters.isFinalized === 'All' ? undefined : (filters.isFinalized === 'Yes'),
  isVerified:  filters.isVerified  === 'All' ? undefined : (filters.isVerified  === 'Yes'),
  activeOnly:  filters.activeStatus === 'All' ? undefined : (filters.activeStatus === 'Show Active'),
})
    const list: any[] = Array.isArray(res?.content) ? res.content : (Array.isArray(res) ? res : [])
    // setInspectionData(list.map(mapInspectionToRow))
    setInspectionData(list.map(mapWithLookups))
  } catch (e) {
    console.error(e)
    setInspectionData([])
    toastApiError(e, 'Failed to load inspections')
  } finally {
    setLoading(false)
  }
}

useEffect(() => {
  if (!lookupsReady) return;
  (async () => {
    setBootLoading(true);
    try {
      await loadInspections();
    } finally {
      setBootLoading(false);
    }
  })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [lookupsReady, effectiveCompanyId]);

useEffect(() => {
  if (bootLoading || loading) {
    setNoDataDelayPassed(false);
    return;
  }
  const t = setTimeout(() => setNoDataDelayPassed(true), 500); // ~0.5s feels nice
  return () => clearTimeout(t);
}, [bootLoading, loading]);


  const handleSearch = () => {
    setShowModal(false);
  };

  const handleCancel = () => {
    setShowModal(false);
  };

const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
  const { name, value } = e.target
  setFilters(prev => {
    const next = { ...prev, [name]: value }
    if (name === 'companyId') {
      next.subcompanyId = ''
      next.vesselId = ''
    }
    if (name === 'subcompanyId') {
      next.vesselId = ''
    }
    return next
  })
}

  // Filter data based on search
const filteredData = useMemo(() => {
  return inspectionData.filter(record => {
    const matchesSearch =
      (record.vessel?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (record.inspection?.toLowerCase() || '').includes(searchTerm.toLowerCase())

    const byCompany =
      !filters.companyId ||
      Number((record as any).companyGroupAdminId) === Number(filters.companyId)

    const bySubcompany =
      !filters.subcompanyId ||
      Number((record as any).companyAdminId) === Number(filters.subcompanyId)

    const byVessel =
      !filters.vesselId ||
      Number((record as any).vesselId) === Number(filters.vesselId)

    const matchesFilters =
      byCompany &&
      bySubcompany &&
      byVessel &&
      (!filters.vesselType || record.vesselType === filters.vesselType) &&
      (!filters.inspection || String((record as any).inspectionKindId) === String(filters.inspection)) &&
      (!filters.inspectionType || record.inspectionType === filters.inspectionType) &&
      (!filters.internalInspector || (record.internalInspector || '').toLowerCase().includes(filters.internalInspector.toLowerCase())) &&
      (!filters.externalInspector || (record.externalInspector || '').toLowerCase().includes(filters.externalInspector.toLowerCase())) &&
      (!filters.fromPort || String((record as any).fromPortId) === String(filters.fromPort)) &&
      (!filters.toPort || String((record as any).toPortId) === String(filters.toPort)) &&
      (!filters.inspectionFromDate || new Date(record.inspectionDate) >= new Date(filters.inspectionFromDate)) &&
      (!filters.inspectionToDate || new Date(record.inspectionDate) <= new Date(filters.inspectionToDate)) &&
      (filters.isFinalized === 'All' || record.isFinalized === filters.isFinalized) &&
      (filters.isVerified === 'All' || record.isVerified === filters.isVerified) &&
      (filters.activeStatus === 'All' ||
        (filters.activeStatus === 'Show Active' && record.isFinalized === 'Yes') ||
        (filters.activeStatus === 'Show Inactive' && record.isFinalized === 'No'))

    return matchesSearch && matchesFilters
  })
}, [searchTerm, filters, inspectionData])

// Utility to extract unique string values for dropdowns
const getUniqueValues = (key: keyof InspectionRecord): string[] => {
  const values = inspectionData
    .map(record => record[key])
    .filter((v): v is string => typeof v === 'string' && v.trim() !== '')

  return Array.from(new Set(values))
}

// Dropdown options
const vesselManagerOptions = getUniqueValues("vesselManager")
const vesselOptionsDropdown = getUniqueValues("vessel")
const vesselTypeOptions = getUniqueValues("vesselType")
const inspectionOptions = getUniqueValues("inspection")
const inspectionTypeOptionsDropdown = getUniqueValues("inspectionType")
const fromPortOptions = getUniqueValues("fromPort")
const toPortOptions = getUniqueValues("toPort")
const internalInspectorOptions = getUniqueValues("internalInspector")
const externalInspectorOptions = getUniqueValues("externalInspector")

const openView = async (id: number) => {
  try {
    const dto = await getInspection(id)

    const patched: InspectionDto = {
      ...dto,
      vesselName: dto.vesselName || vesselMap[dto.vesselId] || `-`,
      vesselType: dto.vesselType || vesselTypeMap[dto.vesselId] || undefined,
      inspectionKindName:
        dto.inspectionKindName ||
        (dto.inspectionKindId != null ? (kindMap[dto.inspectionKindId] || `-`) : ''),
      fromPortName:
        dto.fromPortName || (dto.fromPortId != null ? (portMap[dto.fromPortId] || '') : ''),
      toPortName:
        dto.toPortName || (dto.toPortId != null ? (portMap[dto.toPortId] || '') : ''),
      // free-text only, no crew lookup
      internalInspectorName: dto.internalInspectorName || undefined,
    }

    setViewing(patched)
    setViewOpen(true)
  } catch (e) {
    console.error('Failed to load inspection', e)
    toastApiError(e, 'Failed to load inspection')
  }
}

const openEdit = async (id: number) => {
  try {
    const dto = await getInspection(id)
    setEditing(dto)
    setEditOpen(true)
  } catch (e) {
    console.error('Failed to load inspection', e)
    toastApiError(e, 'Failed to load inspection')
  }
}

// Submit from the full edit modal (PUT)
const handleUpdateInspection = async (form: SubmitShape) => {
  if (!editing) return
  const partial: Partial<InspectionDto> = {
    vesselId: Number(form.vessel),
    inspectionKindId: Number(form.inspection),
    inspectionType: (form.inspectionType as any) || 'DETAILS_TYPE',
    inspectionFromDate: form.inspectionDate,
    inspectionToDate: form.inspectionDate,
    internalInspectorName: form.internalInspectorName || undefined,
    externalInspectorName: form.externalInspector || undefined,
    fromPortId: form.fromPort ? Number(form.fromPort) : undefined,
    toPortId: form.toPort ? Number(form.toPort) : undefined,
    hoursOnboard: form.hoursOnboard ? Number(form.hoursOnboard) : undefined,
    remarks: form.inspectionRemarks || undefined,
  }

  try {
    const updated = await updateInspection(editing.id, partial)
    if (form.attachmentFiles?.length) {
      await uploadInspectionAttachments(editing.id, form.attachmentFiles)
    }
    const oldPlanId = editing.planId ?? (editing as any).inspectionPlanId
    const newPlanId = form.linkToPlan ? Number(form.linkToPlan) : null

    if (oldPlanId && Number(oldPlanId) !== newPlanId) {
      try {
        await unlinkPlan(Number(oldPlanId))
      } catch (err) {
        console.error('Failed to unlink old inspection plan', err)
      }
    }

    if (newPlanId && newPlanId !== Number(oldPlanId)) {
      try {
        await linkPlanToInspection(newPlanId, Number(editing.id))
      } catch (err) {
        console.error('Failed to link inspection plan', err)
      }
    }
    setEditOpen(false)
    setEditing(null)
    await loadInspections()
  } catch (e) {
    console.error('Update failed', e)
    toastApiError(e, 'Failed to update inspection')
  }
}

  const handleDeleteClick = async (record: InspectionRecord) => {
    setRecordToDelete(record)
    setDeleteLoading(true)
    setDeleteModalVisible(true)
    try {
      const fresh = await getInspection(record.id)
      const fCount = fresh.findingsCount ?? 0
      const count = fresh.findingAttachments ? fresh.findingAttachments.length : 0
      setDeleteFindingsCount(fCount)
      setDeleteFindingAttachmentsCount(count)
    } catch {
      const fCount = record.findingsCount ?? 0
      const count = record.findingAttachments ? record.findingAttachments.length : 0
      setDeleteFindingsCount(fCount)
      setDeleteFindingAttachmentsCount(count)
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (!recordToDelete) return
    setDeleteLoading(true)
    try {
      await deleteInspection(recordToDelete.id)
      toast.success('Inspection deleted successfully', {position: 'top-center'})
      setDeleteModalVisible(false)
      setRecordToDelete(null)
      await loadInspections()
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to delete inspection'
      toast.error(msg, {position: 'top-center'})
    } finally {
      setDeleteLoading(false)
    }
  }


  // Apply sorting
  const sortedData = useMemo(() => {
    let sortedRecords = [...filteredData]

    if (sortConfig.key !== null) {
      sortedRecords.sort((a, b) => {
        const aVal = a[sortConfig.key!]
        const bVal = b[sortConfig.key!]

        // Handle different data types
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          const aStr = aVal.toLowerCase()
          const bStr = bVal.toLowerCase()
          if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1
          if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1
          return 0
        }

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal
        }

        if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
          return sortConfig.direction === 'asc' 
            ? (aVal === bVal ? 0 : aVal ? 1 : -1)
            : (aVal === bVal ? 0 : aVal ? -1 : 1)
        }

        // Handle null/undefined values
        if (!aVal && !bVal) return 0
        if (!aVal) return sortConfig.direction === 'asc' ? 1 : -1
        if (!bVal) return sortConfig.direction === 'asc' ? -1 : 1

        return 0
      })
    }

    return sortedRecords
  }, [filteredData, sortConfig])

  const handleSort = (key: keyof InspectionRecord) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  const indexOfLastRecord = currentPage * rowsPerPage
  const indexOfFirstRecord = indexOfLastRecord - rowsPerPage
  const currentRecords = sortedData.slice(indexOfFirstRecord, indexOfLastRecord)
  const totalPages = Math.ceil(sortedData.length / rowsPerPage)

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(e.target.value))
    setCurrentPage(1)
  }

    const handleAddInspection = async (form: SubmitShape, _saveAndAddNew: boolean) => {
    const payload: CreateInspectionPayload = {
      vesselId: Number(form.vessel),
      inspectionKindId: Number(form.inspection),
      inspectionType: (form.inspectionType as any) || 'DETAILS_TYPE',
      inspectionFromDate: form.inspectionDate,
      inspectionToDate: form.inspectionDate,
      internalInspectorName: form.internalInspectorName || undefined,
      externalInspectorName: form.externalInspector || undefined,
      fromPortId: form.fromPort ? Number(form.fromPort) : undefined,
      toPortId: form.toPort ? Number(form.toPort) : undefined,
      hoursOnboard: form.hoursOnboard || undefined,
      remarks: form.inspectionRemarks || undefined,
      linkToPlanId: form.linkToPlan ? Number(form.linkToPlan) : undefined, // server may auto-link
    }

    const created = await createInspection(payload).catch(() => null)
    if (created?.id && form.attachmentFiles?.length) {
      try {
        await uploadInspectionAttachments(Number(created.id), form.attachmentFiles)
      } catch {}
    }

    if (created?.id && form.linkToPlan) {
      try { await linkPlanToInspection(Number(form.linkToPlan), Number(created.id)) } catch {}
    }

    await loadInspections()
  }


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getStatusBadge = (isFinalized: boolean, isVerified: boolean) => {
    if (isVerified) {
      return <span className='badge badge-light-success'>Verified</span>
    } else if (isFinalized) {
      return <span className='badge badge-light-primary'>Finalized</span>
    } else {
      return <span className='badge badge-light-warning'>In Progress</span>
    }
  }

  const getScoreColor = (score?: number) => {
    if (!score) return 'text-muted'
    if (score >= 90) return 'text-success'
    if (score >= 75) return 'text-warning'
    return 'text-danger'
  }

  const truncateText = (text: string, maxLength: number = 100) => {
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
  }

    const handleClearFilters = () => {
  setFilters({
    companyId: '', subcompanyId: '', vesselId: '',
    vesselType: '', inspection: '', inspectionType: '',
    internalInspector: '', externalInspector: '',
    inspectionFromDate: '', inspectionToDate: '',
    fromPort: '', toPort: '',
    activeStatus: 'All', isFinalized: 'All', isVerified: 'All',
  })
}


  return (
    <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
          <div className='card'>
            {/* Header */}
            <div className='card-header border-0 pt-6 d-flex justify-content-between bg-white'>
              <div>
                <h3 className='card-label text-dark fw-bold'>Inspection Index</h3>
              </div>
              <div className='card-toolbar'>
                {canCreateInspection ? (
                  <button
                    type='button'
                    className='btn btn_primary'
                    onClick={() => setIsModalVisible(true)}
                  >
                    <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
                    Add New Inspection Details
                  </button>
                ) : null}
              </div>
            </div>

            {/* Search and Filters */}
            <div className='card-body py-4 bg-white border-top'>
              <div className='row gx-3 gy-3 mb-4'>

               {/* Vessel Type */}
{!shipboardUser && (
  <div className='col-md-2'>
    <label className='form-label fw-semibold fs-7' style={{ color: '#A1A5B7' }}>
      Vessel Type
    </label>
    <SearchableSelect
      className='form-select'
      style={{
        border: '1px solid #E4E6EF',
        borderRadius: '6px',
        fontSize: '14px',
        padding: '8px 12px',
        color: '#5E6278',
      }}
      name='vesselType'
      value={filters.vesselType}
      onChange={handleFilterChange}
    >
      <option value=''>All Vessel Types</option>
      {vesselTypeOptions.map(opt => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </SearchableSelect>
  </div>
)}


                {/* Additional Filters Button */}
                <div className='col-md-3 d-flex align-items-end'>
                  <button
                    type='button'
                    className='btn btn_primary text-nowrap'
                    // style={{
                    //   borderColor: '#E4E6EF',
                    //   color: '#5E6278',
                    //   fontSize: '14px',
                    //   padding: '8px 16px',
                    //   borderRadius: '6px',
                    //   height: 'fit-content'
                    // }}
                    onClick={() => setShowModal(true)}
                  >
                    Additional Filters
                  </button>

                  <button
                    type='button'
                    className='btn text-nowrap'
                    style={{
                      fontSize: '14px',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      height: 'fit-content'
                    }}
                    onClick={handleClearFilters}
                  >
                    Clear All
                  </button>
                </div>
                </div>

                {/* Modal */}
                {showModal && (
                  <div 
                    className="modal fade show d-flex align-items-center justify-content-center" 
                    tabIndex={-1} 
                    style={{ 
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '110%',
                      zIndex: 1050
                    }}
                  >
                    <div className="modal-dialog" style={{ width: '100%', height: '80%' }}>
                      <div className="modal-content">
                        <div className="modal-header">
                          <h5 className="modal-title">Additional Filters</h5>
                          <button 
                            type="button" 
                            className="btn-close" 
                            onClick={handleCancel}
                          ></button>
                        </div>
                        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                          <div className='row gx-4 gy-4'>
                            {/* Inspection Type */}
                            <div className='col-lg-6 col-md-6'>
                              <label className='form-label fw-semibold fs-7 mb-2' style={{ color: '#A1A5B7' }}>
                                Inspection Type
                              </label>
                              <SearchableSelect
                                className='form-select'
                                style={{ border: '1px solid #E4E6EF', borderRadius: '6px', fontSize: '14px', padding: '10px 14px', color: '#5E6278', minHeight: '42px' }}
                                name='inspection'
                                value={filters.inspection}
                                onChange={handleFilterChange}
                              >
                                <option value=''>All Inspection Types</option>
                                {kinds.map(k => (
                                  <option key={k.id} value={String(k.id)}>{k.name}</option>
                                ))}
                              </SearchableSelect>
                            </div>

                            {/* Inspector Name */}
                            <div className='col-lg-6 col-md-6'>
                              <label className='form-label fw-semibold fs-7 mb-2' style={{ color: '#A1A5B7' }}>
                                Inspector Name
                              </label>
                              <input
                                type='text'
                                className='form-control'
                                style={{
                                  border: '1px solid #E4E6EF',
                                  borderRadius: '6px',
                                  fontSize: '14px',
                                  padding: '10px 14px',
                                  color: '#5E6278',
                                  minHeight: '42px'
                                }}
                                name='externalInspector'
                                value={filters.externalInspector}
                                onChange={handleFilterChange}
                                placeholder='Search inspector name'
                              />
                            </div>

                            {/* Inspection From Date */}
                            <div className='col-lg-6 col-md-6'>
                              <label className='form-label fw-semibold fs-7 mb-2' style={{ color: '#A1A5B7' }}>
                                Inspection From Date
                              </label>
                              <input
                                type='date'
                                className='form-control'
                                style={{
                                  border: '1px solid #E4E6EF',
                                  borderRadius: '6px',
                                  fontSize: '14px',
                                  padding: '10px 14px',
                                  color: '#5E6278',
                                  minHeight: '42px'
                                }}
                                name='inspectionFromDate'
                                value={filters.inspectionFromDate}
                                onChange={handleFilterChange}
                              />
                            </div>

                            {/* Inspection To Date */}
                            <div className='col-lg-6 col-md-6'>
                              <label className='form-label fw-semibold fs-7 mb-2' style={{ color: '#A1A5B7' }}>
                                Inspection To Date
                              </label>
                              <input
                                type='date'
                                className='form-control'
                                style={{
                                  border: '1px solid #E4E6EF',
                                  borderRadius: '6px',
                                  fontSize: '14px',
                                  padding: '10px 14px',
                                  color: '#5E6278',
                                  minHeight: '42px'
                                }}
                                name='inspectionToDate'
                                value={filters.inspectionToDate}
                                onChange={handleFilterChange}
                              />
                            </div>

                            {/* From Port */}
                            <div className='col-lg-6 col-md-6'>
                              <label className='form-label fw-semibold fs-7 mb-2' style={{ color: '#A1A5B7' }}>
                                From Port
                              </label>
                              <SearchableSelect
                                className='form-select'
                                style={{
                                  border: '1px solid #E4E6EF',
                                  borderRadius: '6px',
                                  fontSize: '14px',
                                  padding: '10px 14px',
                                  color: '#5E6278',
                                  minHeight: '42px'
                                }}
                                name='fromPort'
                                value={filters.fromPort}
                                onChange={handleFilterChange}
                              >
                                <option value=''>All Ports</option>
                                {fromPortOptions.map(opt => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </SearchableSelect>
                            </div>

                            {/* To Port */}
                            <div className='col-lg-6 col-md-6'>
                              <label className='form-label fw-semibold fs-7 mb-2' style={{ color: '#A1A5B7' }}>
                                To Port
                              </label>
                              <SearchableSelect
                                className='form-select'
                                style={{
                                  border: '1px solid #E4E6EF',
                                  borderRadius: '6px',
                                  fontSize: '14px',
                                  padding: '10px 14px',
                                  color: '#5E6278',
                                  minHeight: '42px'
                                }}
                                name='toPort'
                                value={filters.toPort}
                                onChange={handleFilterChange}
                              >
                                <option value=''>All Ports</option>
                                {toPortOptions.map(opt => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </SearchableSelect>
                            </div>

                            {/* Show Active/Inactive */}
                            <div className='col-lg-6 col-md-6'>
                              <label className='form-label fw-semibold fs-7 mb-2' style={{ color: '#A1A5B7' }}>
                                Show Active/Inactive
                              </label>
                              <SearchableSelect
                                className='form-select'
                                style={{
                                  border: '1px solid #E4E6EF',
                                  borderRadius: '6px',
                                  fontSize: '14px',
                                  padding: '10px 14px',
                                  color: '#5E6278',
                                  minHeight: '42px'
                                }}
                                name='activeStatus'
                                value={filters.activeStatus}
                                onChange={handleFilterChange}
                              >
                                <option value='All'>All</option>
                                <option value='Show Active'>Show Active</option>
                                <option value='Show Inactive'>Show Inactive</option>
                              </SearchableSelect>
                            </div>
                          </div>
                        </div>
                        {/* <div className="modal-footer">
                          <button 
                            type="button" 
                            className="btn btn-secondary" 
                            onClick={handleCancel}
                          >
                            Cancel
                          </button>
                          <button 
                            type="button" 
                            className="btn btn_primary" 
                            onClick={handleSearch}
                          >
                            Search
                          </button>
                        </div> */}
                      </div>
                    </div>
                  </div>
                )}
              {/* Table */}
              <div className='report-table table-responsive' style={{ position: 'relative' }}>
  {loading && <div style={tableOverlayStyle}>{spinner}</div>}
  <div style={{ overflowX: 'auto' }}>
    <table className='table table-bordered align-middle'>
                {/* ---------- THEAD ---------- */}
                <thead className='table-header text-start'>
                  <tr>
                    <th className='text-center' style={{
                      minWidth: '80px',
                      // position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      backgroundColor: '#f8f9fa',
                    }}>SR/NO</th>

                    {/* VESSEL */}
                    <th onClick={() => handleSort('vessel')} className="cursor-pointer" style={{ minWidth: '180px' }}>
                      <div className="d-flex align-items-center align-middle">
                        VESSEL
                        <div style={{ transform: 'translateY(-2px)' }}>
                          <KTSVG
                            path={`/media/map/sort-col-${
                              sortConfig.key === 'vessel'
                                ? sortConfig.direction === 'asc'
                                  ? 'up-black'
                                  : 'down-black'
                                : 'grey'
                            }.svg`}
                            className="svg-icon ms-2 custom-sort-icon"
                          />
                        </div>
                      </div>
                    </th>

                    {/* VESSEL TYPE */}
{!shipboardUser && (
  <th
    onClick={() => handleSort('vesselType')}
    className="cursor-pointer"
    style={{ minWidth: '160px' }}
  >
    <div className="d-flex align-items-center align-middle">
      VESSEL TYPE
      <div style={{ transform: 'translateY(-2px)' }}>
        <KTSVG
          path={`/media/map/sort-col-${
            sortConfig.key === 'vesselType'
              ? sortConfig.direction === 'asc'
                ? 'up-black'
                : 'down-black'
              : 'grey'
          }.svg`}
          className="svg-icon ms-2 custom-sort-icon"
        />
      </div>
    </div>
  </th>
)}

                    {/* INSPECTION TYPE */}
                    <th onClick={() => handleSort('inspection')} className="cursor-pointer" style={{ minWidth: '180px' }}>
                      <div className="d-flex align-items-center">
                        INSPECTION TYPE
                        <div style={{ transform: 'translateY(-2px)' }}>
                          <KTSVG
                            path={`/media/map/sort-col-${
                              sortConfig.key === 'inspection'
                                ? sortConfig.direction === 'asc'
                                  ? 'up-black'
                                  : 'down-black'
                                : 'grey'
                            }.svg`}
                            className="svg-icon ms-2 custom-sort-icon"
                          />
                        </div>
                      </div>
                    </th>

                    {/* INSPECTION DATE */}
                    <th onClick={() => handleSort('inspectionDate')} className="cursor-pointer" style={{ minWidth: '180px' }}>
                      <div className="d-flex align-items-center">
                        INSPECTION DATE
                        <div style={{ transform: 'translateY(-2px)' }}>
                          <KTSVG
                            path={`/media/map/sort-col-${
                              sortConfig.key === 'inspectionDate'
                                ? sortConfig.direction === 'asc'
                                  ? 'up-black'
                                  : 'down-black'
                                : 'grey'
                            }.svg`}
                            className="svg-icon ms-2 custom-sort-icon"
                          />
                        </div>
                      </div>
                    </th>

                    {/* INSPECTOR NAME */}
                    <th onClick={() => handleSort('externalInspector')} className="cursor-pointer" style={{ minWidth: '180px' }}>
                      <div className="d-flex align-items-center">
                        INSPECTOR NAME
                        <div style={{ transform: 'translateY(-2px)' }}>
                          <KTSVG
                            path={`/media/map/sort-col-${
                              sortConfig.key === 'externalInspector'
                                ? sortConfig.direction === 'asc'
                                  ? 'up-black'
                                  : 'down-black'
                                : 'grey'
                            }.svg`}
                            className="svg-icon ms-2 custom-sort-icon"
                          />
                        </div>
                      </div>
                    </th>

                    {/* FROM PORT */}
                    <th onClick={() => handleSort('fromPort')} className="cursor-pointer" style={{ minWidth: '140px' }}>
                      <div className="d-flex align-items-center">
                        FROM PORT
                        <div style={{ transform: 'translateY(-2px)' }}>
                          <KTSVG
                            path={`/media/map/sort-col-${
                              sortConfig.key === 'fromPort'
                                ? sortConfig.direction === 'asc'
                                  ? 'up-black'
                                  : 'down-black'
                                : 'grey'
                            }.svg`}
                            className="svg-icon ms-2 custom-sort-icon"
                          />
                        </div>
                      </div>
                    </th>

                    {/* TO PORT */}
                    <th onClick={() => handleSort('toPort')} className="cursor-pointer" style={{ minWidth: '140px' }}>
                      <div className="d-flex align-items-center">
                        TO PORT
                        <div style={{ transform: 'translateY(-2px)' }}>
                          <KTSVG
                            path={`/media/map/sort-col-${
                              sortConfig.key === 'toPort'
                                ? sortConfig.direction === 'asc'
                                  ? 'up-black'
                                  : 'down-black'
                                : 'grey'
                            }.svg`}
                            className="svg-icon ms-2 custom-sort-icon"
                          />
                        </div>
                      </div>
                    </th>

                    {/* COUNT OF NCRs */}
                    <th onClick={() => handleSort('countNCRsObservations')} className="cursor-pointer text-center" style={{ minWidth: '250px' }}>
                      <div className="d-flex align-items-center justify-content-center">
                        COUNT OF NCRs AND OBSERVATIONS
                        <div style={{ transform: 'translateY(-2px)' }}>
                          <KTSVG
                            path={`/media/map/sort-col-${
                              sortConfig.key === 'countNCRsObservations'
                                ? sortConfig.direction === 'asc'
                                  ? 'up-black'
                                  : 'down-black'
                                : 'grey'
                            }.svg`}
                            className="svg-icon ms-2 custom-sort-icon"
                          />
                        </div>
                      </div>
                    </th>

                    {/* COUNT OF NCRs COMPLETED */}
                    <th onClick={() => handleSort('countNCRsObservationsCompleted')} className="cursor-pointer text-center" style={{ minWidth: '280px' }}>
                      <div className="d-flex align-items-center justify-content-center">
                        COUNT OF NCRs AND OBSERVATIONS COMPLETED
                        <div style={{ transform: 'translateY(-2px)' }}>
                          <KTSVG
                            path={`/media/map/sort-col-${
                              sortConfig.key === 'countNCRsObservationsCompleted'
                                ? sortConfig.direction === 'asc'
                                  ? 'up-black'
                                  : 'down-black'
                                : 'grey'
                            }.svg`}
                            className="svg-icon ms-2 custom-sort-icon"
                          />
                        </div>
                      </div>
                    </th>

                    {/* ACTIONS (sticky right) */}
                    <th
                      style={{
                        minWidth: '150px',
                        position: 'static',
                        right: 0,
                        backgroundColor: '#f8f9fa',
                        zIndex: 2,
                        textAlign: 'center',
                      }}
                    >
                      ACTIONS
                    </th>
                  </tr>
                </thead>

                {/* ---------- TBODY ---------- */}
                <tbody className='table-body text-start'>
  {(bootLoading || loading) ? (
    // 🔁 Skeleton: 8 shimmering rows like CrewingList
    Array.from({length: 8}).map((_, i) => (
      <tr key={`skeleton-${i}`}>
        {Array.from({length: shipboardUser ? 10 : 11}).map((__, c) => (
          <td key={`s-${i}-${c}`} className="py-3">
            <div className="placeholder-wave">
              <div className="placeholder w-100" style={{height: 14, borderRadius: 4}} />
            </div>
          </td>
        ))}
      </tr>
    ))
  ) : (currentRecords.length === 0 && noDataDelayPassed) ? (
    <tr>
      <td colSpan={shipboardUser ? 10 : 11} className='text-center text-muted py-5'>
        No inspection records found for the selected criteria.
      </td>
    </tr>
  ) : (
    currentRecords.map((record, index) => (
                      <tr key={record.id}>
                        <td className='text-center' style={{
                          minWidth: '80px',
                          // position: 'sticky',
                          left: 0,
                          // backgroundColor: '#fff',
                          zIndex: 2,
                        }}>{index + 1}</td>
                        <td className="text-dark fw-bold fs-6 text-nowrap" style={{ minWidth: '180px' }}>
                          {record.vessel}
                        </td>
                        {!shipboardUser && (
  <td className="text-dark fs-6 text-nowrap" style={{ minWidth: '160px' }}>
    {record.vesselType}
  </td>
)}
                        <td className="text-dark fs-6 text-nowrap" style={{ minWidth: '180px' }}>
                          {record.inspection}
                        </td>
                        <td className="text-dark fs-6 text-nowrap" style={{ minWidth: '180px' }}>
                          {formatDate(record.inspectionDate)}
                        </td>
                        <td className="text-dark fs-6 text-nowrap" style={{ minWidth: '180px' }}>
                          {record.externalInspector || '-'}
                        </td>
                        <td className="text-dark fs-6 text-nowrap" style={{ minWidth: '140px' }}>
                          {record.fromPort || '-'}
                        </td>
                        <td className="text-dark fs-6 text-nowrap" style={{ minWidth: '140px' }}>
                          {record.toPort || '-'}
                        </td>
                        <td className="text-center text-dark fw-bold fs-6 text-nowrap" style={{ minWidth: '250px' }}>
                          {record.countNCRsObservations}
                        </td>
                        <td className="text-center text-nowrap" style={{ minWidth: '280px' }}>
                          <div className="d-flex align-items-center justify-content-center">
                            <span className="text-dark fw-bold fs-6 me-2">
                              {record.countNCRsObservationsCompleted}
                            </span>
                            <div className="progress" style={{ width: '60px', height: '6px' }}>
                              <div
                                className="progress-bar bg-success"
                                style={{
                                  width: `${
                                    record.countNCRsObservations > 0
                                      ? (record.countNCRsObservationsCompleted / record.countNCRsObservations) * 100
                                      : 0
                                  }%`,
                                }}
                              />
                            </div>
                          </div>
                        </td>

                       {/* ACTIONS (sticky right) */}
<td
  style={{
    minWidth: '150px',
    position: 'static',
    right: 0,
    // backgroundColor: '#fff',
    textAlign: 'center',
    zIndex: 1,
  }}
>
  <div className='d-flex align-items-center justify-content-center  text-nowrap'>
    {canReadInspection ? (
      <button
        className='btn btn-icon btn-sm me-2'
        title='View'
        onClick={() => openView(record.id)}
      >
        <KTSVG path='/media/map/ph_eye.svg' className='svg-icon-3 text-primary' />
      </button>
    ) : null}

    {canUpdateInspection ? (
      <button
        className='btn btn-icon btn-sm me-2'
        title='Edit'
        onClick={() => openEdit(record.id)}
      >
        <KTSVG path='/media/map/edit-active.svg' className='' />
      </button>
    ) : null}

    {canDeleteInspection ? (
      <button
        className='btn btn-icon btn-sm'
        title='Delete'
        onClick={() => handleDeleteClick(record)}
      >
        <KTSVG path='/media/map/trash.svg' className='' />
      </button>
    ) : null}
  </div>
</td>

                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              </div>


                {/* Pagination */}
                <div 
                  className='pagination-wrapper d-flex justify-content-between align-items-center py-3'
                  style={{
                    position: 'static',
                    bottom: 0,
                    backgroundColor: '#fff',
                    zIndex: 10,
                    borderTop: '1px solid #dee2e6',
                    marginTop: 'auto'
                  }}
                >
                  <div className='d-flex align-items-center'>
                    <span className='text-muted me-2'>Rows per page</span>
                    <SearchableSelect
                      className='form-select'
                      style={{
                        borderRadius: '20px',
                        width: '70px',
                        border: '1px solid #dee2e6',
                        fontSize: '14px',
                        padding: '4px 8px'
                      }}
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </SearchableSelect>
                  </div>
                  <div className='d-flex align-items-center'>
                    <span className='text-muted me-3' style={{ fontSize: '14px' }}>
                      Showing <strong>{((currentPage - 1) * rowsPerPage) + 1}-{Math.min(currentPage * rowsPerPage, sortedData.length)}</strong> of <strong>{sortedData.length}</strong>
                    </span>

                    <nav>
                      <ul className='pagination pagination-sm mb-0' style={{ gap: '2px' }}>
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className='page-link text-muted'
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              padding: '8px 12px',
                              fontSize: '14px',
                              borderRadius: '6px'
                            }}
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            ‹
                          </button>
                        </li>

                        {(() => {
                          const pages = []
                          const showPages = 5
                          let startPage = Math.max(1, currentPage - 2)
                          let endPage = Math.min(totalPages, startPage + showPages - 1)

                          if (endPage - startPage < showPages - 1) {
                            startPage = Math.max(1, endPage - showPages + 1)
                          }

                          if (startPage > 1) {
                            pages.push(
                              <li key={1} className='page-item'>
                                <button
                                  className='page-link text-muted'
                                  style={{
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #dee2e6',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    minWidth: '40px',
                                    borderRadius: '6px'
                                  }}
                                  onClick={() => handlePageChange(1)}
                                >
                                  1
                                </button>
                              </li>
                            )

                            if (startPage > 2) {
                              pages.push(
                                <li key='ellipsis1' className='page-item disabled'>
                                  <span className='page-link border-0 text-muted' style={{ backgroundColor: 'transparent', padding: '4px 8px' }}>...</span>
                                </li>
                              )
                            }
                          }

                          for (let i = startPage; i <= endPage; i++) {
                            pages.push(
                              <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                                <button
                                  className='page-link text-muted'
                                  style={{
                                    backgroundColor: currentPage === i ? '#F4F9FF' : 'transparent',
                                    border: '1px solid #dee2e6',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    minWidth: '40px',
                                    borderRadius: '6px',
                                    outline: 'none',
                                    boxShadow: 'none'
                                  }}
                                  onClick={() => handlePageChange(i)}
                                >
                                  {i}
                                </button>
                              </li>
                            )
                          }

                          if (endPage < totalPages) {
                            if (endPage < totalPages - 1) {
                              pages.push(
                                <li key='ellipsis2' className='page-item disabled'>
                                  <span className='page-link border-0 text-muted' style={{ backgroundColor: 'transparent', padding: '4px 8px' }}>...</span>
                                </li>
                              )
                            }

                            pages.push(
                              <li key={totalPages} className='page-item'>
                                <button
                                  className='page-link text-muted'
                                  style={{
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #dee2e6',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    minWidth: '40px',
                                    borderRadius: '6px'
                                  }}
                                  onClick={() => handlePageChange(totalPages)}
                                >
                                  {totalPages}
                                </button>
                              </li>
                            )
                          }

                          return pages
                        })()}

                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button
                            className='page-link text-muted'
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              padding: '8px 12px',
                              fontSize: '14px',
                              borderRadius: '6px'
                            }}
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            ›
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Inspection Modal */}
      <AddInspectionModal
  visible={isModalVisible}
  onClose={() => setIsModalVisible(false)}
  onSubmit={handleAddInspection}
  // pass the full vessel list (with company ids) — modal will narrow it locallly
  vessels={vessels}
  kinds={kinds}
  ports={ports}
  planOptions={planOptions}
  showCompanyFilters={!shipboardUser}
  isCrew={shipboardUser}
  // NEW
  companies={companies}
  subcompanies={subcompanies}
  crews={crews}
  rankMap={rankMap}
/>

{/* View Modal */}
<ViewInspectionModal
  visible={viewOpen}
  onClose={() => { setViewOpen(false); setViewing(null) }}
  record={viewing}
/>

{/* Full Edit Modal (same fields as Add) */}
<EditInspectionModal
  visible={editOpen}
  onClose={() => { setEditOpen(false); setEditing(null) }}
  record={editing}
  onSubmit={handleUpdateInspection}
  vessels={vessels}
  kinds={kinds}
  ports={ports}
  showCompanyFilters={!shipboardUser}
  isCrew={shipboardUser}
  companies={companies}
  subcompanies={subcompanies}
  crews={crews}
  rankMap={rankMap}
/>

{/* Delete Confirmation & Warning Modal */}
<DeleteRecordConfirmModal
  visible={deleteModalVisible}
  recordType='Inspection'
  vesselName={recordToDelete?.vessel}
  findingsCount={deleteFindingsCount}
  findingAttachmentsCount={deleteFindingAttachmentsCount}
  loading={deleteLoading}
  onClose={() => {
    setDeleteModalVisible(false)
    setRecordToDelete(null)
    setDeleteFindingsCount(0)
    setDeleteFindingAttachmentsCount(0)
  }}
  onConfirm={handleConfirmDelete}
/>

    </div>
  )
}

export default InspectionIndex
